/**
 * Der Kontostand — genau genommen keine Einstellung, sondern der Startpunkt
 * jeder Projektion.
 *
 * Er steht trotzdem hier: es ist der eine Wert, den ein Mensch von Hand
 * nachzieht, wenn der Import ihn nicht schon gesetzt hat. Eintraege werden
 * angehaengt, nie ueberschrieben — deshalb der Verlauf darunter.
 */

import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Betragsfeld, Button, formatDatum, formatEuro, toast } from '@elbklar/ui';

import { TextField } from '@/components/Felder';
import { balanceApi } from '@/lib/api';
import { betragFehler } from '@/lib/betrag';
import { datum, hatFehler, ohne, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import { heuteIso } from '@/lib/format';

export function KontostandForm() {
  const queryClient = useQueryClient();

  const historie = useQuery({
    queryKey: ['balance-history'],
    queryFn: balanceApi.list,
  });

  // `betrag` ist der Decimal-Text aus dem Betragsfeld, `null` solange keine Zahl dasteht.
  const [saldo, setSaldo] = useState({ betrag: '' as string | null, stichtag: heuteIso(), notiz: '' });
  const [fehler, setFehler] = useState<Fehler<'betrag' | 'stichtag'>>({});
  // Nach dem Speichern neu aufbauen: Das Betragsfeld übernimmt einen von außen
  // geleerten Wert nicht, solange es den Fokus hat (Absenden mit Enter). Sonst
  // stünde „190000“ weiter da, und ein zweites Absenden meldete „Betrag fehlt“
  // unter einer sichtbaren Zahl.
  const [durchlauf, setDurchlauf] = useState(0);

  const saldoSetzen = useMutation({
    // Decimal-Text aus dem Betragsfeld, von `onSubmit` geprüft.
    mutationFn: (betrag: string) =>
      balanceApi.create({
        as_of: saldo.stichtag,
        amount: betrag,
        note: saldo.notiz.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Kontostand eingetragen');
      setSaldo({ betrag: '', stichtag: heuteIso(), notiz: '' });
      setFehler({});
      setDurchlauf((n) => n + 1);
      void queryClient.invalidateQueries({ queryKey: ['balance-history'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: ['projection'] });
    },
    onError: () => toast.error('Speichern fehlgeschlagen'),
  });

  return (
    <div className="max-w-2xl">
      <p className="mb-4 text-muted-foreground">
        Startpunkt jeder Projektion. Ein Import setzt ihn automatisch; hier lässt
        er sich jederzeit korrigieren. Einträge werden angehängt, nie
        überschrieben.
      </p>

      <form noValidate
        key={durchlauf}
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          const neu = {
            betrag: betragFehler(saldo.betrag),
            stichtag: datum(saldo.stichtag, { unlesbar: unlesbareFelder(e.currentTarget).has('stichtag') }),
          };
          setFehler(neu);
          if (hatFehler(neu) || saldo.betrag === null) {
            zumErstenFehler(e.currentTarget);
            return;
          }
          saldoSetzen.mutate(saldo.betrag);
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Betragsfeld
            label="Kontostand (€)"
            placeholder="190000"
            hinweis={(wert) => (wert === null ? 'Tausenderpunkt und Komma sind erlaubt: 190.000,50' : undefined)}
            error={fehler.betrag}
            required
            value={saldo.betrag ?? ''}
            onChange={(betrag) => {
              setFehler((f) => ohne(f, 'betrag'));
              setSaldo({ ...saldo, betrag });
            }}
          />
          <TextField
            label="Stand vom"
            type="date"
            name="stichtag"
            value={saldo.stichtag}
            onChange={(e) => {
              setSaldo({ ...saldo, stichtag: e.target.value });
              setFehler((f) => ohne(f, 'stichtag'));
            }}
            error={fehler.stichtag}
            required
          />
        </div>
        <TextField
          label="Notiz"
          placeholder="z. B. Kreditauszahlung"
          value={saldo.notiz}
          onChange={(e) => setSaldo({ ...saldo, notiz: e.target.value })}
        />
        <Button type="submit" variant="primary" loading={saldoSetzen.isPending}>
          Kontostand eintragen
        </Button>
      </form>

      {historie.data && historie.data.length > 0 && (
        <div className="mt-6 border-t border-border pt-4">
          <h3 className="mb-2 t-label text-muted-foreground">Verlauf</h3>
          <ul className="space-y-1">
            {historie.data.slice(0, 8).map((eintrag) => (
              <li key={eintrag.id} className="flex items-center justify-between gap-3">
                <span className="tabular">
                  {formatDatum(eintrag.as_of)}
                  <span className="ml-2 t-small text-muted-foreground">
                    {eintrag.source === 'import' ? 'aus Import' : 'manuell'}
                  </span>
                </span>
                <span className="font-medium tabular">{formatEuro(eintrag.amount, { cent: true })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
